import TiaraTracker from '@kakao/tiara-sdk-javascript';

import {
  TIARA_ACTION,
  TIARA_PAGE,
  TIARA_PAGE_CLICK,
  TIARA_SECTION_BY_PAGE_KEY,
  TiaraActionKey,
  TiaraPageClickKey,
  TiaraPageKey
} from '@/constants/tiara';
import { isDev } from '@/utils/phase';

type meta = { id?: string; name?: string; type?: string };
export type PageMeta = meta;
type EventMeta = meta;

const SVC_DOMAIN = 'partner-wallet.kakao.com';

class EventTracker {
  private tracker?: TiaraTracker = undefined;

  constructor() {
    const tiaraTracker = window.TiaraTracker;
    if (!tiaraTracker) return;

    // Constructor에서 기본 tiara 설정 완료
    this.tracker = tiaraTracker
      .getInstance()
      .setAutoClick(true)
      .setSvcDomain(SVC_DOMAIN)
      .setDeployment(isDev ? 'dev' : 'production');
  }

  initialize() {
    if (!this.tracker) return;
    // @ts-expect-error - https://kakao.agit.in/g/435929/wall/405551861#comment_panel_438592046
    this.tracker.setPageMeta();
  }

  setPage(pageKey: TiaraPageKey | TiaraPageClickKey): EventTracker {
    if (!this.tracker) return this;

    this.initialize();
    const pageKeyStr = { ...TIARA_PAGE, ...TIARA_PAGE_CLICK }[pageKey];

    this.tracker.setPage(pageKeyStr);
    return this;
  }

  trackPage({ pageKey, actionKey }: { pageKey: TiaraPageKey; actionKey?: TiaraActionKey }) {
    if (!this.tracker) return this;
    const action = TIARA_ACTION[actionKey || `${pageKey}_VIEW`];
    this.tracker.trackPage(action);
    return this;
  }

  trackEvent(actionKey: TiaraActionKey) {
    if (!this.tracker) return this;
    const eventKeyStr = TIARA_ACTION[actionKey];
    this.tracker.trackEvent(eventKeyStr);
    return this;
  }

  // 페이지 전용
  setSection(pageKey: TiaraPageKey) {
    if (!this.tracker) return this;
    const section = TIARA_SECTION_BY_PAGE_KEY[pageKey];
    this.tracker.setSection(section);
    return this;
  }

  actionKind(actionKind: 'ClickContent'): EventTracker {
    if (!this.tracker) return this;
    this.tracker.actionKind(actionKind);
    return this;
  }

  // pageMeta 구성 요소가 있을 때만 pageMeta 적용
  pageMeta(pageMeta?: PageMeta): EventTracker {
    if (!pageMeta || !this.tracker || this.isEmpty(pageMeta)) return this;
    this.tracker.pageMeta(pageMeta);
    return this;
  }

  // eventMeta 구성 요소가 있을 때만 eventMeta 적용
  eventMeta(eventMeta?: EventMeta): EventTracker {
    if (!eventMeta || !this.tracker || this.isEmpty(eventMeta)) return this;
    this.tracker.eventMeta(eventMeta);
    return this;
  }

  // 커스텀 속성 추가
  customProps(props?: Record<string, unknown>): EventTracker {
    if (!this.tracker || !props || this.isEmpty(props)) return this;
    this.tracker.customProps(props);
    return this;
  }

  // 최종 실행
  track() {
    if (!this.tracker) return;
    this.tracker.track();
  }

  private isEmpty(obj: Record<string, unknown>) {
    return Object.keys(obj).length === 0;
  }
}

const tracker = new EventTracker();

export default tracker;
